import { supabase } from '../lib/supabase';

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (err) {
      lastError = err as Error;
      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt);
        console.log(`Retry attempt ${attempt + 1} after ${delay}ms`);
        await wait(delay);
      }
    }
  }
  
  throw lastError;
}

export async function checkSubscription(calendarId: string, userId: string): Promise<boolean> {
  try {
    // Validate inputs
    if (!calendarId || !userId) {
      console.warn('Invalid parameters for subscription check:', { calendarId, userId });
      return false;
    }

    const { data, error } = await supabase
      .from('subscriptions')
      .select('id')
      .eq('calendar_id', calendarId)
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      // Log error but don't throw - return false instead
      console.error('Error checking subscription:', error);
      return false;
    }

    return !!data;
  } catch (err) {
    // Catch any unexpected errors
    console.error('Unexpected error checking subscription:', err);
    return false;
  }
}

export async function createFreeSubscription(calendarId: string, userId: string): Promise<void> {
  try {
    // Validate inputs
    if (!calendarId || !userId) {
      throw new Error('Invalid parameters: calendarId and userId are required');
    }

    // Check if calendar exists
    const { data: calendar, error: calendarError } = await supabase
      .from('calendars')
      .select('id, payment_type')
      .eq('id', calendarId)
      .single();

    if (calendarError || !calendar) {
      throw new Error('Calendar not found or inaccessible');
    }

    if (calendar.payment_type !== 'free') {
      throw new Error('This calendar requires a paid subscription');
    }

    // Check for existing subscription
    const existingSubscription = await checkSubscription(calendarId, userId);
    if (existingSubscription) {
      throw new Error('You are already subscribed to this calendar');
    }

    // Insert subscription first
    const { error: subscriptionError } = await supabase
      .from('subscriptions')
      .insert({
        calendar_id: calendarId,
        user_id: userId,
        status: 'active'
      });

    if (subscriptionError) {
      if (subscriptionError.message.includes('duplicate key')) {
        throw new Error('You are already subscribed to this calendar');
      }
      throw subscriptionError;
    }

    // Send notification with retry logic
    await retryWithBackoff(async () => {
      const { error: notificationError } = await supabase.functions.invoke(
        'send-subscription-notification',
        {
          body: { calendarId, subscriberId: userId }
        }
      );

      if (notificationError) {
        console.error('Error sending subscription notification:', {
          error: notificationError,
          params: { calendarId, userId }
        });
        // Don't throw here - notification failure shouldn't prevent subscription
        console.warn('Failed to send notification but subscription was created');
      }
    });

  } catch (err) {
    console.error('Error creating free subscription:', err);
    throw err;
  }
}